import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Aussehen = 'primaer' | 'sekundaer' | 'still' | 'gefahr';
type Groesse = 'normal' | 'gross' | 'klein';

export interface KnopfProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  aussehen?: Aussehen;
  groesse?: Groesse;
  laedt?: boolean;
  symbol?: ReactNode;
  volleBreite?: boolean;
}

const AUSSEHEN: Record<Aussehen, string> = {
  primaer:
    'bg-marke-600 text-white hover:bg-marke-700 active:bg-marke-800 disabled:bg-marke-300 shadow-sm',
  sekundaer:
    'bg-white text-slate-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 active:bg-slate-100 dunkel:bg-slate-800 dunkel:text-slate-100 dunkel:ring-slate-600 dunkel:hover:bg-slate-700',
  still:
    'bg-transparent text-slate-700 hover:bg-slate-100 active:bg-slate-200 dunkel:text-slate-200 dunkel:hover:bg-slate-800',
  gefahr: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 disabled:bg-red-300 shadow-sm',
};

// Alle Größen halten die 44-px-Vorgabe für Touch-Ziele ein (SPEC Kap. 12).
const GROESSE: Record<Groesse, string> = {
  klein: 'min-h-touch px-3 text-sm gap-1.5',
  normal: 'min-h-touch px-4 text-base gap-2',
  gross: 'min-h-14 px-6 text-lg gap-2.5',
};

export const Knopf = forwardRef<HTMLButtonElement, KnopfProps>(function Knopf(
  {
    aussehen = 'primaer',
    groesse = 'normal',
    laedt = false,
    symbol,
    volleBreite = false,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled === true || laedt}
      aria-busy={laedt || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        'touch-manipulation select-none',
        AUSSEHEN[aussehen],
        GROESSE[groesse],
        volleBreite && 'w-full',
        className,
      )}
      {...rest}
    >
      {laedt ? (
        <span
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      ) : (
        symbol
      )}
      {children}
    </button>
  );
});
